import { ReactEditor, useSlate, RenderElementProps } from 'slate-react';
import { useEditorMode } from 'app/state/contexts/EditorContext';
import { useTagSelection } from 'app/state/contexts/TagContext';
import { useSidebar } from 'app/state/contexts/SidebarContext';
import { getBlockTagBindings, hasBlockTags } from 'slate/extensions/tag/types/block';
import { BlockTagChrome } from 'slate/extensions/tag/components/BlockTagChrome';
import { useBlockTagColors, textBlockTagRegionStyle } from 'slate/extensions/tag/utils/useBlockTagColors';

interface BlockTaggableListProps {
  as: 'ul' | 'ol';
  attributes: RenderElementProps['attributes'] & { className?: string };
  children: React.ReactNode;
  element: RenderElementProps['element'];
  elementUUID: string;
}

// Renders a <ul>/<ol> list carrier. In tagging mode, clicking it arms it for the scope
// toggle and, if tagged, also opens the tag-info sidebar. A tagged list shows the
// same BlockTagBadge as tables and images. Uses ReactEditor.findPath, not
// editor.selection (blind inside the editor's shadow root — see
// [[feedback-slate-selection-async]]).
export const BlockTaggableList = ({ as: Tag, attributes, children, element, elementUUID }: BlockTaggableListProps) => {
  const editor = useSlate();
  const { isTaggingMode, taggingSubMode } = useEditorMode();
  const { setBlockSelection, clearBlockSelection, setClickedTagIds } = useTagSelection();
  const { setIsSidebarOpen } = useSidebar();

  const bindings = getBlockTagBindings(element);
  const isBlockTagged = isTaggingMode && bindings.length > 0 && hasBlockTags(element);
  const blockTagColors = useBlockTagColors(bindings.length > 0 ? bindings : undefined);
  const showBlockChrome = isBlockTagged && blockTagColors && !blockTagColors.isHidden;
  const blockTagVisualStyle = showBlockChrome ? textBlockTagRegionStyle(blockTagColors) : null;

  const handleBlockClick = (e: React.MouseEvent) => {
    if (!isTaggingMode || taggingSubMode === 'mark-coverage' || !elementUUID) return;
    if ((e?.target as Element)?.closest?.('.ixbrl-tag')) {
      clearBlockSelection?.();
      return;
    }
    try {
      const path = ReactEditor.findPath(editor, element);
      setBlockSelection?.({ kind: 'list', path, elementId: elementUUID });
    } catch (err) {
      console.error(`Element is not attached to the editor tree yet ${err}`);
    }
    if (bindings.length > 0) {
      setClickedTagIds?.(
        bindings.map((b) => b.tagId),
        { fromContent: true }
      );
      setIsSidebarOpen?.(8);
    }
  };

  const className = [attributes.className, (element as { formatting?: { class?: string } }).formatting?.class].filter(Boolean).join(' ') || undefined;

  const list = (
    <Tag {...attributes} className={className} data-element-uuid={elementUUID} onClick={isBlockTagged ? undefined : handleBlockClick}>
      {children}
    </Tag>
  );

  if (!isBlockTagged) return list;

  return (
    <div style={{ position: 'relative', ...(blockTagVisualStyle ?? {}) }} onClick={handleBlockClick}>
      {list}
      {showBlockChrome && <BlockTagChrome bindings={bindings} indicatorColor={blockTagColors.indicator} />}
    </div>
  );
};
